import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import Testimonials from '../home/components/Testimonials';
import MidPageCTA from '../home/components/MidPageCTA';

interface LocationFAQ { question: string; answer: string; }
interface LocationPageProps {
  city: string; stateAbbr?: string; heroImage: string;
  description: string; geoDescription: string;
  mapEmbedSrc: string; faqs: LocationFAQ[];
}

const allServices = [
  { icon: 'ri-road-map-line', name: 'Paver Driveways', href: '/services/paver-driveways' },
  { icon: 'ri-home-4-line', name: 'Paver Patios', href: '/services/paver-patios' },
  { icon: 'ri-walk-line', name: 'Walkways', href: '/services/walkways' },
  { icon: 'ri-water-flash-line', name: 'Pool Deck Pavers', href: '/services/pool-deck' },
  { icon: 'ri-layout-column-line', name: 'Retaining Walls', href: '/services/retaining-walls' },
  { icon: 'ri-fire-line', name: 'Outdoor Living', href: '/services/outdoor-living' },
];

const processSteps = [
  { icon: 'ri-user-voice-line', title: 'Free Consultation', desc: 'On-site evaluation — no commitment.' },
  { icon: 'ri-pencil-ruler-2-line', title: 'Custom Design', desc: 'Tailored to your property and goals.' },
  { icon: 'ri-stack-line', title: 'Material Selection', desc: 'Premium brands, expert guidance.' },
  { icon: 'ri-tools-fill', title: 'ICPI Installation', desc: 'Certified crew, industry standards.' },
  { icon: 'ri-checkbox-circle-fill', title: 'Final Walkthrough', desc: '100% satisfaction guaranteed.' },
];

const whyPoints = [
  'ICPI certified specialists', 'Premium paver materials',
  'Proper base preparation', 'Clean, professional crew',
  'On time & on budget', 'Transparent pricing',
];

export default function LocationPageTemplate({
  city, stateAbbr = 'FL', heroImage, description, geoDescription, mapEmbedSrc, faqs,
}: LocationPageProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <>
      <Navbar />
      <main>
        {/* 1. HERO */}
        <section className="relative min-h-[70vh] flex items-end pb-20"
          style={{ backgroundImage: `url('${heroImage}')`, backgroundSize: 'cover', backgroundPosition: 'center top' }}>
          <div className="absolute inset-0 bg-gradient-to-t from-parkside-darker/90 via-parkside-darker/45 to-parkside-darker/15" />
          <div className="absolute inset-0 bg-gradient-to-r from-parkside-darker/50 to-transparent" />
          <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
            <div className="flex flex-wrap gap-2 mb-5">
              {['✔ Local Tampa Bay Specialists', '✔ ICPI Certified', '✔ Free Estimates'].map((b) => (
                <span key={b} className="bg-white/10 backdrop-blur-sm text-white/90 text-xs font-semibold px-3.5 py-1.5 rounded-full border border-white/20">{b}</span>
              ))}
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-[68px] text-white max-w-3xl mb-5 leading-tight tracking-display">
              Paver Installation in {city}, {stateAbbr}
            </h1>
            <p className="text-white/75 text-lg max-w-xl mb-8 leading-relaxed">{description}</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="bg-parkside-amber text-white font-bold px-8 py-4 rounded-xl hover:bg-parkside-amber/90 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer">
                Get a Free Estimate <span className="w-5 h-5 inline-flex items-center justify-center"><i className="ri-arrow-right-line" /></span>
              </Link>
              <a href="tel:813-404-6313" className="bg-white/12 backdrop-blur-sm border border-white/30 text-white font-bold px-8 py-4 rounded-xl hover:bg-white/22 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-phone-fill" /> (813) 404-6313
              </a>
            </div>
          </div>
        </section>

        {/* 2. TRUST BAR */}
        <section className="bg-white border-b border-stone-100">
          <div className="max-w-7xl mx-auto px-6 py-5">
            <div className="flex flex-wrap items-center justify-center gap-8">
              {[
                { icon: 'ri-star-fill', label: '5-Star Rated on Google & Yelp', color: 'text-parkside-amber' },
                { icon: 'ri-map-pin-2-fill', label: `Serving ${city} & Nearby`, color: 'text-parkside-green' },
                { icon: 'ri-award-fill', label: 'ICPI Certified Installer', color: 'text-parkside-amber' },
                { icon: 'ri-shield-check-fill', label: 'Licensed, Bonded & Insured', color: 'text-parkside-green' },
              ].map((t) => (
                <div key={t.label} className="flex items-center gap-2.5">
                  <div className="w-8 h-8 flex items-center justify-center bg-parkside-cream rounded-lg flex-shrink-0">
                    <i className={`${t.icon} ${t.color} text-base`} />
                  </div>
                  <span className="font-semibold text-parkside-dark text-sm">{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 3. LOCAL SERVICES */}
        <section className="py-24 bg-parkside-warm">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="inline-block bg-parkside-amber/12 text-parkside-amber text-xs font-bold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-3">What We Offer in {city}</span>
              <h2 className="font-display text-4xl md:text-5xl text-parkside-dark tracking-display leading-tight">Paver Services in {city}</h2>
              <p className="text-parkside-muted mt-3 max-w-xl mx-auto text-base">{geoDescription}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {allServices.map((s) => (
                <Link key={s.name} to={s.href} className="group bg-white rounded-2xl p-6 border border-stone-100 hover:border-parkside-green/30 hover:bg-parkside-cream transition-all cursor-pointer">
                  <div className="w-11 h-11 flex items-center justify-center bg-parkside-green/10 rounded-xl mb-4 group-hover:bg-parkside-green/20 transition-colors">
                    <i className={`${s.icon} text-parkside-green text-xl`} />
                  </div>
                  <p className="text-parkside-dark font-bold text-sm mb-1.5 group-hover:text-parkside-green transition-colors">{s.name}</p>
                  <div className="flex items-center gap-1 text-parkside-amber text-xs font-semibold">
                    Learn More <i className="ri-arrow-right-line text-xs" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* MID-PAGE CTA */}
        <MidPageCTA headline={`Serving ${city} — Get Your Free On-Site Estimate`} subtext="Professional paver installation with ICPI-certified crew. No commitment required." />

        {/* 4. AI ANSWER BLOCK */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-parkside-cream rounded-2xl p-8 md:p-10 border border-stone-100">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-parkside-amber/12 rounded-lg">
                    <i className="ri-robot-line text-parkside-amber text-lg" />
                  </div>
                  <span className="text-parkside-amber text-xs font-bold uppercase tracking-widest">Local Expertise</span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-parkside-dark mb-5 tracking-display leading-tight">
                  Paver Installation in {city}, {stateAbbr}
                </h2>
                <p className="text-parkside-muted text-base leading-relaxed mb-4">
                  Parkside Pavers provides paver installation services in {city}, offering custom patios, driveways, and hardscape solutions designed for local climate conditions and property styles. Our team serves homeowners and businesses throughout {city} with ICPI-certified installation and premium materials.
                </p>
                <p className="text-parkside-muted text-base leading-relaxed">
                  From driveway pavers in {city} to custom patio and pool deck installations, Parkside Pavers brings the same level of craftsmanship to every project — backed by 5-star reviews and years of Tampa Bay experience.
                </p>
              </div>
              <div className="bg-parkside-darker rounded-2xl p-8 md:p-10">
                <h3 className="text-white font-bold text-xl mb-5">Why {city} Homeowners Choose Pavers</h3>
                <ul className="space-y-3.5">
                  {['Durable in Florida\'s heat and humidity', 'Better drainage than concrete', 'Lower long-term maintenance costs', 'Increases curb appeal & property value', 'Individually repairable if damaged', 'Endless design & color options'].map((b) => (
                    <li key={b} className="flex items-center gap-3 text-sm text-white/70">
                      <div className="w-6 h-6 flex items-center justify-center bg-parkside-amber/15 rounded-lg flex-shrink-0">
                        <i className="ri-checkbox-circle-fill text-parkside-amber text-sm" />
                      </div>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* 5. PROCESS */}
        <section className="py-24 bg-parkside-warm">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <span className="inline-block bg-parkside-green/10 text-parkside-green text-xs font-bold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-3">How It Works</span>
              <h2 className="font-display text-4xl md:text-5xl text-parkside-dark tracking-display leading-tight">Our Process in {city}</h2>
            </div>
            <div className="hidden lg:grid grid-cols-5 gap-6 relative">
              <div className="absolute top-10 left-[calc(10%+28px)] right-[calc(10%+28px)] h-px bg-gradient-to-r from-parkside-amber/30 via-parkside-green to-parkside-amber/30" />
              {processSteps.map((step, idx) => (
                <div key={step.title} className="flex flex-col items-center text-center">
                  <div className="relative mb-6">
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center relative z-10 ${idx % 2 === 0 ? 'bg-parkside-amber' : 'bg-parkside-green'}`}>
                      <i className={`${step.icon} text-white text-2xl`} />
                    </div>
                    <div className="absolute -top-2 -right-1 w-7 h-7 bg-white border-2 border-parkside-dark rounded-full flex items-center justify-center z-20">
                      <span className="text-parkside-dark font-bold text-[10px]">0{idx + 1}</span>
                    </div>
                  </div>
                  <h3 className="font-bold text-parkside-dark text-sm mb-2 px-2">{step.title}</h3>
                  <p className="text-parkside-stone text-xs leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
            <div className="lg:hidden space-y-0">
              {processSteps.map((step, idx) => (
                <div key={step.title} className="flex gap-5">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center z-10 ${idx % 2 === 0 ? 'bg-parkside-amber' : 'bg-parkside-green'}`}>
                      <i className={`${step.icon} text-white text-lg`} />
                    </div>
                    {idx < processSteps.length - 1 && <div className="w-px flex-1 bg-stone-200 my-2" />}
                  </div>
                  <div className="pb-8 pt-1">
                    <span className="text-xs font-bold text-parkside-amber">0{idx + 1}</span>
                    <h3 className="font-bold text-parkside-dark text-base mt-0.5 mb-1">{step.title}</h3>
                    <p className="text-parkside-muted text-sm">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 6. WHY CHOOSE US */}
        <section className="py-24 bg-parkside-darker">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
              <div>
                <span className="inline-block bg-parkside-amber/20 text-parkside-gold text-xs font-bold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-4">Why Parkside</span>
                <h2 className="font-display text-4xl md:text-5xl text-white mb-8 tracking-display leading-tight">Why {city} Homeowners Choose Parkside Pavers</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mb-8">
                  {whyPoints.map((p) => (
                    <div key={p} className="flex items-center gap-3">
                      <div className="w-7 h-7 flex items-center justify-center bg-parkside-amber/15 rounded-lg flex-shrink-0">
                        <i className="ri-checkbox-circle-fill text-parkside-gold text-sm" />
                      </div>
                      <span className="text-white/75 text-sm">{p}</span>
                    </div>
                  ))}
                </div>
                <Link to="/contact" className="inline-flex items-center gap-2 bg-parkside-amber text-white font-bold px-7 py-3.5 rounded-xl hover:bg-parkside-amber/90 transition-all whitespace-nowrap cursor-pointer">
                  Get a Free Estimate in {city} <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="h-[380px] rounded-2xl overflow-hidden">
                <img src={heroImage} alt={`Paver installation in ${city} FL`} className="w-full h-full object-cover object-top" />
              </div>
            </div>
          </div>
        </section>

        {/* 7. LOCAL AREA / MAP */}
        <section className="py-24 bg-parkside-cream">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block bg-parkside-green/12 text-parkside-green text-xs font-bold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-4">Serving {city}</span>
                <h2 className="font-display text-4xl md:text-5xl text-parkside-dark mb-4 tracking-display leading-tight">
                  Neighborhoods Throughout {city}
                </h2>
                <p className="text-parkside-muted leading-relaxed mb-6">
                  Parkside Pavers serves neighborhoods throughout {city} and surrounding communities. Whether you&apos;re in a waterfront home, a suburban neighborhood, or a commercial property, we bring the same level of care and craftsmanship to every project.
                </p>
                <div className="space-y-3 mb-8">
                  {['Free on-site consultation & estimate', 'ICPI certified installation crew', 'Premium paver materials & brands', 'Projects completed on time & on budget'].map((p) => (
                    <div key={p} className="flex items-center gap-3">
                      <div className="w-7 h-7 flex items-center justify-center bg-parkside-green/15 rounded-lg flex-shrink-0">
                        <i className="ri-checkbox-circle-fill text-parkside-green text-sm" />
                      </div>
                      <span className="text-parkside-muted text-sm">{p}</span>
                    </div>
                  ))}
                </div>
                <Link to="/contact" className="inline-flex items-center gap-2 bg-parkside-green text-white font-bold px-7 py-3.5 rounded-xl hover:bg-parkside-greenDark transition-all whitespace-nowrap cursor-pointer">
                  Get a Free Estimate in {city} <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="h-[400px] rounded-2xl overflow-hidden border border-stone-200 relative">
                <iframe title={`Paver installation in ${city} FL`} src={mapEmbedSrc} width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                <div className="absolute top-4 left-4 bg-white rounded-xl px-4 py-2.5 border border-stone-100">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 flex items-center justify-center bg-parkside-green rounded-lg">
                      <i className="ri-map-pin-fill text-white text-sm" />
                    </div>
                    <div>
                      <p className="text-parkside-dark font-bold text-xs">Parkside Pavers</p>
                      <p className="text-parkside-stone text-[11px]">{city}, {stateAbbr}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 8. TESTIMONIALS */}
        <Testimonials />

        {/* 9. FAQ */}
        <section className="py-24 bg-parkside-warm">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
              <div className="lg:col-span-2">
                <span className="inline-block bg-parkside-amber/12 text-parkside-amber text-xs font-bold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-4">Voice Search FAQs</span>
                <h2 className="font-display text-4xl text-parkside-dark mb-5 tracking-display leading-tight">FAQs About Paver Installation in {city}</h2>
                <div className="bg-white rounded-2xl p-5 border border-stone-100">
                  <a href="tel:813-404-6313" className="flex items-center gap-3 group cursor-pointer">
                    <div className="w-10 h-10 flex items-center justify-center bg-parkside-green/15 rounded-xl flex-shrink-0">
                      <i className="ri-phone-fill text-parkside-green text-lg" />
                    </div>
                    <div>
                      <p className="text-xs text-parkside-stone font-semibold uppercase tracking-wider">Call Direct</p>
                      <p className="text-parkside-dark font-bold group-hover:text-parkside-green transition-colors">(813) 404-6313</p>
                    </div>
                  </a>
                </div>
              </div>
              <div className="lg:col-span-3 space-y-2">
                {faqs.map((faq, idx) => (
                  <div key={idx} className={`rounded-2xl border overflow-hidden ${openFaq === idx ? 'border-parkside-green/30 bg-white' : 'border-stone-100 bg-white'}`}>
                    <button className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left cursor-pointer" onClick={() => setOpenFaq(openFaq === idx ? null : idx)}>
                      <div className="flex items-start gap-3">
                        <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 ${openFaq === idx ? 'bg-parkside-green text-white' : 'bg-parkside-cream text-parkside-muted'}`}>
                          <i className="ri-question-line text-xs" />
                        </div>
                        <span className="text-parkside-dark font-semibold text-sm leading-snug">{faq.question}</span>
                      </div>
                      <span className="w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-full bg-parkside-cream">
                        <i className={`text-parkside-green text-sm ${openFaq === idx ? 'ri-subtract-line' : 'ri-add-line'}`} />
                      </span>
                    </button>
                    {openFaq === idx && (
                      <div className="px-6 pb-6">
                        <div className="ml-9 border-l-2 border-parkside-green/30 pl-4">
                          <p className="text-parkside-muted text-sm leading-relaxed">{faq.answer}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 10. FINAL CTA */}
        <section className="py-16 bg-parkside-green">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <span className="inline-block bg-white/15 text-white text-xs font-bold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-4">Serving {city}</span>
            <h2 className="font-display text-4xl md:text-5xl text-white mb-4 tracking-display leading-tight">Ready to Start Your Project in {city}?</h2>
            <p className="text-white/70 text-lg mb-8 max-w-xl mx-auto">Get a free on-site estimate from Parkside Pavers — {city}&apos;s trusted paver installation specialists.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="bg-white text-parkside-green font-bold px-8 py-4 rounded-xl hover:bg-white/90 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2">
                Request Free Estimate <i className="ri-arrow-right-line" />
              </Link>
              <a href="tel:813-404-6313" className="border-2 border-white/40 text-white font-bold px-8 py-4 rounded-xl hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2">
                <i className="ri-phone-fill" /> Call Now
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
